/**
 * "ההיסטוריה שלך ›" — the row at the foot of a metric's logging
 * drawer, and what replaced the היום·היסטוריה tab strip.
 *
 * ── WHY A ROW AND NOT A TAB
 *
 * The tab strip made "today" a named destination, which is what the drawer
 * already was: the tile shows the number, the drawer is where she acts on it. So
 * the strip spent ~72px of a 90vh sheet restating the drawer's own identity, and
 * it put history one level inside a LOGGING surface — where the member who most
 * needs history (2,049 have water history; 43 logged water in the last
 * fortnight) has no reason to go. A row costs nothing when ignored, and history
 * now has its own route for the member whose verb is "see", not "log".
 *
 * ── IT RENDERS ABOVE THE CHALLENGE LOCK, WHICH IS THE WHOLE POINT (D2)
 *
 * `MetricsSection` renders `FeatureLockedMessage` INSTEAD of the tracker content
 * for a challenge-locked metric. This row is a sibling of that branch, never a
 * child of it: history is reachable "regardless of subscription, challenge
 * enrolment, or course-only status", and a member between challenges still owns
 * what she recorded. That property is exactly what the tab shell was protecting,
 * and losing it in the move to a route would be the one real regression
 * available here.
 *
 * ── IT STILL COUNTS, AND IT NO LONGER SAYS THE NUMBER
 *
 * The row used to carry `12 ימים` at its far edge, argued for as evidence: a
 * member who assumes the app forgot her taps a number, not a label. The owner
 * cut it — "no need for the how many days collected" — and the argument for it
 * was weaker than it looked, because this is the THIRD surface that number
 * reached on one path and the only one where it decorated a door rather than
 * labelled its own contents.
 *
 * The read behind it stays and is not waste. It populates the exact query key
 * `/history/:metric` uses, so the tap that follows renders with no spinner at
 * all, and it still decides whether this row appears.
 *
 * ── THE ROW MAY NOT LIE WHILE IT DOES NOT KNOW
 *
 * It hides ONLY on a settled, successful read of zero logged days. While loading,
 * and on an error, it renders anyway — because `days: []` on a failed read would
 * otherwise remove the one signpost saying her history exists, on the evidence
 * of a dropped request.
 */

import { ChevronLeft } from 'lucide-react';

import { useMetricLifetime } from '@/hooks/useMetricHistory';
import { isLoggedDay } from '@/lib/metricDays';
import type { MetricKey } from '@/lib/metricLogging';
import { cn } from '@/lib/utils';

const COPY = { label: 'ההיסטוריה שלך' } as const;

export interface MetricHistoryTeaserViewProps {
  metric: MetricKey;
  /**
   * Logged days on record, or `null` while unknown (loading, or a failed read).
   *
   * A GATE NOW, NOT A LABEL — the row no longer prints it. Only the settled
   * zero suppresses the row, which is why `null` may not be collapsed into it:
   * "we do not know yet" and "there is nothing" are different answers, and
   * rendering the second on the first would delete her signpost on the evidence
   * of a dropped request.
   */
  loggedDays: number | null;
  onOpen: () => void;
  className?: string;
}

export const MetricHistoryTeaserView = ({
  metric,
  loggedDays,
  onOpen,
  className,
}: MetricHistoryTeaserViewProps) => {
  // A settled zero is the one case with nothing to point at. `null` is "we do
  // not know yet", which is not the same answer and must not render the same.
  if (loggedDays === 0) return null;

  return (
    <button
      type="button"
      dir="rtl"
      data-history-teaser={metric}
      onClick={onOpen}
      className={cn(
        'flex w-full min-h-[52px] items-center justify-between gap-3 rounded-2xl bg-muted-warm px-4 py-3',
        'transition-all duration-200 active:scale-[0.98]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[hsl(var(--primary-strong))] focus-visible:outline-offset-2',
        className,
      )}
    >
      <span className="text-base font-medium text-foreground">{COPY.label}</span>
      <ChevronLeft
        aria-hidden="true"
        className="h-5 w-5 shrink-0 text-muted-foreground"
        strokeWidth={2}
      />
    </button>
  );
};

export interface MetricHistoryTeaserProps {
  metric: MetricKey;
  onOpen: () => void;
  className?: string;
}

const MetricHistoryTeaser = ({ metric, onOpen, className }: MetricHistoryTeaserProps) => {
  const { days, isLoading, isError } = useMetricLifetime(metric);
  const loggedDays = isLoading || isError ? null : days.filter(isLoggedDay).length;

  return (
    <MetricHistoryTeaserView
      metric={metric}
      loggedDays={loggedDays}
      onOpen={onOpen}
      className={className}
    />
  );
};

export default MetricHistoryTeaser;
